import * as THREE from 'three';
import { MAX_FRAME_SECONDS } from './timeSmoothing.js';
import { createWebGLRenderer, hasWebGL } from './webgl.js';

const vertexShader = /* glsl */ `
varying vec2 vUv;
varying vec3 vPosition;

void main() {
  vUv = uv;
  vPosition = position;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const fragmentShader = /* glsl */ `
uniform sampler2D uDataTexture;
uniform sampler2D uTexture;
uniform vec4 resolution;
varying vec2 vUv;

void main() {
  vec2 uv = vUv;
  vec4 offset = texture2D(uDataTexture, vUv);
  gl_FragColor = texture2D(uTexture, uv - 0.02 * offset.rg);
}
`;

// The effect was tuned frame by frame on a 120Hz screen. Each frame there
// (a "step" below) took the latest pointer position Chrome had coalesced for
// it and set the velocity to the distance from the previous one; then every
// cell's displacement faded to `relaxation` of itself, the cells near the
// pointer were pushed by the velocity, and the velocity shrank to 0.9 of
// itself. To look the same at any refresh rate the effect keeps its own clock
// of 120 steps a second and replays each step from the pointer reports
// timestamped by then (getCoalescedEvents brings every report of a frame), so
// a step is what a 120Hz frame was, whatever the screen shows: two steps on a
// 60Hz frame, a step on most frames at 144Hz, and on the others the previous
// step's picture, as a 120Hz screen would still be showing it.
const STEPS_PER_SECOND = 120;
const STEP_SECONDS = 1 / STEPS_PER_SECOND;
const VELOCITY_KEPT_PER_STEP = 0.9;
// A step due within this share of a step of a frame runs on the frame, so a
// 120Hz screen whose frame times wobble still takes one step a frame, at the
// frame's time, which the per-frame code amounted to.
const SNAP_STEPS = 0.1;
// A long frame (tab switch, jank) takes at most this many steps, as long as
// the frame clocks elsewhere let a frame count (MAX_FRAME_SECONDS).
const MAX_STEPS_PER_FRAME = Math.round(MAX_FRAME_SECONDS * STEPS_PER_SECOND);
// A frame longer than a step runs several steps at once, by when the reports
// for all of them are in. A step with no report of its own whose next report
// is already in takes the position on the way there, as a device reporting
// every step would have reported it: a Mac merges the mouse to one report per
// screen refresh, so on a 60Hz screen every other step misses the move the
// same mouse reports there on a 120Hz one. Pauses stay pauses: a device that
// has shown it reports at least once a step (a gap of a step or less, two
// reports on one timestamp included, among its last RECENT_GAPS) moves the
// pointer only with its reports, and so does any device when the next report
// comes more than FRAMES_PER_REPORT screen refreshes after the last.
const RECENT_GAPS = 8;
const FRAMES_PER_REPORT = 1.5;
// The screen refresh is the shortest of the last RECENT_FRAMES frames, so a
// janky frame doesn't stretch it.
const RECENT_FRAMES = 8;
// Gaps longer than a frame may be are pauses, not the device's interval.
const MAX_REPORT_GAP_STEPS = MAX_STEPS_PER_FRAME;
// Reports a step apart up to floating-point rounding count as one step apart.
const SAME_STEP = 1e-6;

/**
 * The pointer as the steps read it, the per-frame code's mouse state (position,
 * the position its velocity was last taken from, velocity per step) plus the
 * reports and leaves not yet stepped through, in the order they came, the last
 * report stepped through and the recent gaps between reports, in steps.
 */
export function createPointer() {
  // The first move pushes from the centre, as the per-frame code did.
  return {
    x: 0.5,
    y: 0.5,
    prevX: 0.5,
    prevY: 0.5,
    vX: 0,
    vY: 0,
    inside: false,
    queue: [],
    from: null,
    lastReportSeconds: null,
    reportGapSteps: [],
  };
}

// A pointer report at (x, y), 0–1 in the container with y up, timestamped
// `seconds`.
export function reportMove(pointer, x, y, seconds) {
  const last = pointer.lastReportSeconds;
  if (last !== null) {
    const gap = (seconds - last) * STEPS_PER_SECOND;
    if (gap >= 0 && gap <= MAX_REPORT_GAP_STEPS) {
      pointer.reportGapSteps.push(gap);
      if (pointer.reportGapSteps.length > RECENT_GAPS) pointer.reportGapSteps.shift();
    }
  }
  pointer.lastReportSeconds = last === null ? seconds : Math.max(last, seconds);
  pointer.queue.push({ x, y, seconds });
}

// The pointer left the container at `seconds`. The first report back inside
// measures no gap: the time outside isn't how often the device reports.
export function reportLeave(pointer, seconds) {
  pointer.lastReportSeconds = null;
  pointer.queue.push({ leave: true, seconds });
}

// The per-frame code's mousemove handler: the velocity is the distance from
// the position it was last taken from.
function moveTo(pointer, x, y) {
  pointer.vX = x - pointer.prevX;
  pointer.vY = y - pointer.prevY;
  pointer.x = x;
  pointer.y = y;
  pointer.prevX = x;
  pointer.prevY = y;
  pointer.inside = true;
}

// The per-frame code's mouseleave handler.
function leave(pointer) {
  pointer.vX = 0;
  pointer.vY = 0;
  pointer.inside = false;
  pointer.from = null;
}

// Where a step at `seconds` with no report of its own sees the pointer: on the
// way from the last report to the next one when that is already in and the
// device merely reports less often than once a step, else null (no move).
function onTheWay(pointer, seconds, refreshSeconds) {
  const { from, queue, reportGapSteps } = pointer;
  const next = queue[0];
  if (!pointer.inside || !from || !next || next.leave) return null;
  if (next.seconds - from.seconds > FRAMES_PER_REPORT * refreshSeconds) return null;
  if (reportGapSteps.some((gap) => gap <= 1 + SAME_STEP)) return null;
  const k = (seconds - from.seconds) / (next.seconds - from.seconds);
  return { x: from.x + (next.x - from.x) * k, y: from.y + (next.y - from.y) * k };
}

/**
 * One step at `seconds`: the per-frame code's frame, given what a 120Hz screen
 * had received by then. Chrome coalesces the reports of a frame into one
 * mousemove with the latest position and flushes it before a mouseleave, so
 * each run of reports between leaves counts as its latest; a step with none
 * may take a position on the way to the next (see onTheWay). It touches
 * neither the DOM nor the clock, so a simulation can drive it directly.
 *
 * @param {Float32Array} data RGBA per cell; R and G hold the displacement
 * @param {ReturnType<typeof createPointer>} pointer the reports and leaves due
 *   by `seconds` are consumed
 * @param {number} seconds the step's time, on the reports' clock, later than
 *   the step before (advanceDistortion keeps them in order)
 * @param {{size: number, mouse: number, strength: number, relaxation: number}} settings
 *   relaxation is the share of displacement kept per step, 0–1
 * @param {number} [refreshSeconds] the screen's refresh interval; 0 (unknown)
 *   takes no position on the way
 */
export function stepDistortion(
  data,
  pointer,
  seconds,
  { size, mouse, strength, relaxation },
  refreshSeconds = 0
) {
  const { queue } = pointer;
  let consumed = 0;
  let latest = null;
  while (consumed < queue.length && queue[consumed].seconds <= seconds) {
    const event = queue[consumed++];
    if (event.leave) {
      if (latest) moveTo(pointer, latest.x, latest.y);
      latest = null;
      leave(pointer);
    } else {
      latest = event;
    }
  }
  queue.splice(0, consumed);
  if (latest) {
    moveTo(pointer, latest.x, latest.y);
    pointer.from = latest;
  } else if (consumed === 0) {
    const at = onTheWay(pointer, seconds, refreshSeconds);
    if (at) moveTo(pointer, at.x, at.y);
  }

  for (let i = 0; i < size * size; i++) {
    data[i * 4] *= relaxation;
    data[i * 4 + 1] *= relaxation;
  }

  const gridMouseX = size * pointer.x;
  const gridMouseY = size * pointer.y;
  const maxDist = size * mouse;

  for (let i = 0; i < size; i++) {
    for (let j = 0; j < size; j++) {
      const dx = gridMouseX - i;
      const dy = gridMouseY - j;
      const distSq = dx * dx + dy * dy;
      if (distSq < maxDist * maxDist) {
        const index = 4 * (i + size * j);
        const power = Math.min(maxDist / Math.sqrt(distSq || 0.0001), 10);
        data[index] += strength * 100 * pointer.vX * power;
        data[index + 1] -= strength * 100 * pointer.vY * power;
      }
    }
  }

  // Decay velocity so a stationary cursor doesn't keep pushing the grid
  pointer.vX *= VELOCITY_KEPT_PER_STEP;
  pointer.vY *= VELOCITY_KEPT_PER_STEP;
}

// The steps' clock: when the next step is due, on the reports' clock (null
// until the first frame, which takes a step), and the recent frame times.
export function createStepClock() {
  return { next: null, lastFrame: null, frameGapSeconds: [] };
}

/**
 * Takes the steps due by a frame at `now` seconds (the requestAnimationFrame
 * timestamp, on the reports' clock): every 1/120 s, the last one moved onto
 * the frame when it is due within SNAP_STEPS of it either way, at most
 * MAX_STEPS_PER_FRAME. A frame more than SNAP_STEPS before the next step
 * takes none.
 *
 * @returns {number} how many steps were taken
 */
export function advanceDistortion(data, pointer, clock, now, settings) {
  if (!Number.isFinite(now)) return 0;
  if (clock.lastFrame !== null && now > clock.lastFrame) {
    clock.frameGapSeconds.push(now - clock.lastFrame);
    if (clock.frameGapSeconds.length > RECENT_FRAMES) clock.frameGapSeconds.shift();
  }
  clock.lastFrame = now;
  const refreshSeconds = clock.frameGapSeconds.length ? Math.min(...clock.frameGapSeconds) : 0;
  const snap = SNAP_STEPS * STEP_SECONDS;
  let next = clock.next ?? now;
  // A longer frame skips the steps before its last few, keeping to the same
  // 1/120 s grid; the reports from then are taken by the first step it takes.
  const stepsDue = Math.floor((now + snap - next) / STEP_SECONDS) + 1;
  if (stepsDue > MAX_STEPS_PER_FRAME) next += (stepsDue - MAX_STEPS_PER_FRAME) * STEP_SECONDS;
  let steps = 0;
  while (steps < MAX_STEPS_PER_FRAME && next <= now + snap) {
    const at = next >= now - snap ? now : next;
    stepDistortion(data, pointer, at, settings, refreshSeconds);
    steps++;
    next = at + STEP_SECONDS;
  }
  clock.next = next;
  return steps;
}

export function initGridDistortion(container, options = {}) {
  const {
    grid = 15,
    mouse = 0.1,
    strength = 0.15,
    relaxation = 0.9,
    imageSrc,
    eventTarget = container,
  } = options;

  if (!container || !imageSrc || !hasWebGL()) return () => {};

  const scene = new THREE.Scene();
  // Nothing is in the DOM yet, so if the renderer won't start the <picture>
  // simply stays — what touch and reduced-motion visitors see (ADR-002).
  const renderer = createWebGLRenderer({
    antialias: true,
    alpha: true,
    powerPreference: 'high-performance',
  });
  if (!renderer) return () => {};
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setClearColor(0x000000, 0);

  const canvas = renderer.domElement;
  Object.assign(canvas.style, {
    position: 'absolute',
    inset: '0',
    width: '100%',
    height: '100%',
    display: 'block',
    pointerEvents: 'none',
  });
  container.appendChild(canvas);

  const camera = new THREE.OrthographicCamera(0, 0, 0, 0, -1000, 1000);
  camera.position.z = 2;

  const uniforms = {
    resolution: { value: new THREE.Vector4() },
    uTexture: { value: null },
    uDataTexture: { value: null },
  };

  const size = grid;
  const data = new Float32Array(4 * size * size);
  // Start at rest (zeros). Mouse interaction injects displacement; relaxation
  // fades it back. Random init showed visible grid blocks before the first
  // few hundred frames could settle.
  const dataTexture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.FloatType);
  dataTexture.needsUpdate = true;
  uniforms.uDataTexture.value = dataTexture;

  const material = new THREE.ShaderMaterial({
    side: THREE.DoubleSide,
    uniforms,
    vertexShader,
    fragmentShader,
    transparent: true,
  });
  const geometry = new THREE.PlaneGeometry(1, 1, size - 1, size - 1);
  const plane = new THREE.Mesh(geometry, material);
  scene.add(plane);

  let imageAspect = 1;
  let loadedTexture = null;

  const handleResize = () => {
    const rect = container.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    if (!width || !height) return;
    const containerAspect = width / height;
    renderer.setSize(width, height);

    // Mimic object-fit: cover. The camera frustum is (containerAspect × 1);
    // the plane must keep the IMAGE's aspect ratio and be scaled up until it
    // covers the frustum, letting the longer axis overflow (= crop).
    //   wider container  → fill the width, overflow top/bottom
    //   taller container → fill the height, overflow left/right
    // The old "taller" branch kept the plane at frustum width and stretched
    // it vertically instead, so a landscape hero on a phone/tablet was drawn
    // squeezed to ~40% of its natural width.
    let planeScaleX = containerAspect;
    let planeScaleY = 1;
    if (imageAspect && containerAspect > 0) {
      if (containerAspect > imageAspect) {
        planeScaleX = containerAspect;
        planeScaleY = containerAspect / imageAspect;
      } else {
        planeScaleX = imageAspect;
        planeScaleY = 1;
      }
    }
    plane.scale.set(planeScaleX, planeScaleY, 1);

    const frustumHeight = 1;
    const frustumWidth = frustumHeight * containerAspect;
    camera.left = -frustumWidth / 2;
    camera.right = frustumWidth / 2;
    camera.top = frustumHeight / 2;
    camera.bottom = -frustumHeight / 2;
    camera.updateProjectionMatrix();

    uniforms.resolution.value.set(width, height, 1, 1);
  };

  const textureLoader = new THREE.TextureLoader();
  textureLoader.load(
    imageSrc,
    (texture) => {
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      imageAspect = texture.image.width / texture.image.height;
      uniforms.uTexture.value = texture;
      loadedTexture = texture;
      container.classList.add('has-distortion');
      handleResize();
    },
    undefined,
    () => {
      // texture load failed — fall back to static img
      if (container.contains(canvas)) container.removeChild(canvas);
    }
  );

  let resizeObserver = null;
  if (window.ResizeObserver) {
    resizeObserver = new ResizeObserver(() => handleResize());
    resizeObserver.observe(container);
  } else {
    window.addEventListener('resize', handleResize);
  }

  const pointer = createPointer();
  const settings = { size, mouse, strength, relaxation };

  // Event timestamps share the requestAnimationFrame clock. A report can't
  // come later than now; one stamped so would sit in the queue past its step.
  const reportSeconds = (e) => Math.min(e.timeStamp, performance.now()) / 1000;

  // Everything but touch (mouse, pen, and untrusted events with no type): a
  // touch drag scrolls the page, and the effect is for devices that hover
  // (ADR-002).
  const handlePointerMove = (e) => {
    if (e.pointerType === 'touch') return;
    const rect = container.getBoundingClientRect();
    // The event target (the whole hero, say) can take the pointer while the
    // container has no area. There is no position on it then, and dividing by
    // zero would put an infinite velocity into the displacement for good.
    if (!rect.width || !rect.height) return;
    // Every report since the last pointermove, each with its own timestamp; an
    // event with none (untrusted, or outside a secure context, where the
    // method is missing) stands for itself.
    const coalesced = e.getCoalescedEvents?.() ?? [];
    for (const report of coalesced.length > 0 ? coalesced : [e]) {
      const x = (report.clientX - rect.left) / rect.width;
      const y = 1 - (report.clientY - rect.top) / rect.height;
      reportMove(pointer, x, y, reportSeconds(report));
    }
  };

  const handlePointerLeave = (e) => {
    if (e.pointerType === 'touch') return;
    reportLeave(pointer, reportSeconds(e));
  };

  eventTarget.addEventListener('pointermove', handlePointerMove, { passive: true });
  eventTarget.addEventListener('pointerleave', handlePointerLeave);

  handleResize();

  const clock = createStepClock();
  let animationId = null;
  const animate = (now) => {
    animationId = requestAnimationFrame(animate);
    const texData = dataTexture.image.data;
    if (!(texData instanceof Float32Array)) return;

    // A frame between steps shows the last step, as a 120Hz screen would.
    if (advanceDistortion(texData, pointer, clock, now / 1000, settings) > 0) {
      dataTexture.needsUpdate = true;
    }
    renderer.render(scene, camera);
  };
  // Started from requestAnimationFrame so every frame, the first included,
  // gets the timestamp the clock needs.
  animationId = requestAnimationFrame(animate);

  return () => {
    if (animationId) cancelAnimationFrame(animationId);
    if (resizeObserver) resizeObserver.disconnect();
    else window.removeEventListener('resize', handleResize);
    eventTarget.removeEventListener('pointermove', handlePointerMove);
    eventTarget.removeEventListener('pointerleave', handlePointerLeave);
    renderer.dispose();
    renderer.forceContextLoss();
    if (container.contains(canvas)) container.removeChild(canvas);
    geometry.dispose();
    material.dispose();
    dataTexture.dispose();
    if (loadedTexture) loadedTexture.dispose();
    container.classList.remove('has-distortion');
  };
}
